import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import authService, { User } from "../services/auth";
import userService, {
  UserProfile,
  UserPreferences,
  UserStats,
} from "../services/api/userService";

interface UserContextState {
  // Auth state
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  user: User | null;

  // Profile data
  profile: UserProfile | null;
  preferences: UserPreferences | null;
  stats: UserStats | null;

  // Auth actions
  login: (
    email: string,
    password: string,
    rememberMe?: boolean,
  ) => Promise<void>;
  logout: () => Promise<void>;
  register: (
    username: string,
    email: string,
    password: string,
  ) => Promise<void>;

  // Profile actions
  updateProfile: (data: Partial<UserProfile>) => Promise<void>;
  updatePreferences: (data: Partial<UserPreferences>) => Promise<void>;
  updateAvatar: (file: File) => Promise<void>;

  // Refresh state
  refreshProfile: () => Promise<void>;
  refreshStats: () => Promise<void>;
}

const UserContext = createContext<UserContextState | undefined>(undefined);

export const UserProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(
    authService.isAuthenticated(),
  );
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<User | null>(authService.getCurrentUser());

  // Profile data
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [stats, setStats] = useState<UserStats | null>(null);

  // Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      if (!user) {
        setIsLoading(false);
        return;
      }

      try {
        const [profileData, preferencesData, statsData] = await Promise.all([
          userService.getProfile(user.id),
          userService.getPreferences(),
          userService.getStats(user.id),
        ]);

        setProfile(profileData);
        setPreferences(preferencesData);
        setStats(statsData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to load user data",
        );
      } finally {
        setIsLoading(false);
      }
    };

    loadInitialData();
  }, [user]);

  // Auth actions
  const login = async (email: string, password: string, rememberMe = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const loggedInUser = await authService.login({
        email,
        password,
        rememberMe,
      });
      setUser(loggedInUser);
      setIsAuthenticated(true);

      // Load user data after login
      const [profileData, preferencesData, statsData] = await Promise.all([
        userService.getProfile(loggedInUser.id),
        userService.getPreferences(),
        userService.getStats(loggedInUser.id),
      ]);

      setProfile(profileData);
      setPreferences(preferencesData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Login failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.logout();
      setUser(null);
      setIsAuthenticated(false);
      setProfile(null);
      setPreferences(null);
      setStats(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Logout failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (
    username: string,
    email: string,
    password: string,
  ) => {
    setIsLoading(true);
    setError(null);

    try {
      const newUser = await authService.register({
        username,
        email,
        password,
        confirmPassword: password, // Assuming password confirmation is handled in the UI
      });
      setUser(newUser);
      setIsAuthenticated(true);

      // Initialize profile data for new user
      const [profileData, preferencesData, statsData] = await Promise.all([
        userService.getProfile(newUser.id),
        userService.getPreferences(),
        userService.getStats(newUser.id),
      ]);

      setProfile(profileData);
      setPreferences(preferencesData);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Registration failed");
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // Profile actions
  const updateProfile = async (data: Partial<UserProfile>) => {
    if (!user) return;

    setError(null);
    try {
      const updatedProfile = await userService.updateProfile(user.id, data);
      setProfile(updatedProfile);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update profile");
      throw err;
    }
  };

  const updatePreferences = async (data: Partial<UserPreferences>) => {
    setError(null);
    try {
      const updatedPreferences = await userService.updatePreferences(data);
      setPreferences(updatedPreferences);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to update preferences",
      );
      throw err;
    }
  };

  const updateAvatar = async (file: File) => {
    if (!user) return;

    setError(null);
    try {
      const { url } = await userService.updateAvatar(user.id, file);
      setProfile((prev) => (prev ? { ...prev, profileImage: url } : null));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update avatar");
      throw err;
    }
  };

  // Refresh functions
  const refreshProfile = useCallback(async () => {
    if (!user) return;

    setError(null);
    try {
      const profileData = await userService.getProfile(user.id);
      setProfile(profileData);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to refresh profile",
      );
      throw err;
    }
  }, [user]);

  const refreshStats = useCallback(async () => {
    if (!user) return;

    setError(null);
    try {
      const statsData = await userService.getStats(user.id);
      setStats(statsData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to refresh stats");
      throw err;
    }
  }, [user]);

  // Token validation on mount and periodic check
  useEffect(() => {
    const validateAuth = async () => {
      try {
        // Check if the token is still valid locally first
        if (!authService.isAuthenticated()) {
          // Token is expired or missing - clear state
          setUser(null);
          setIsAuthenticated(false);
          setProfile(null);
          setPreferences(null);
          setStats(null);
          return;
        }
        // Token exists and is not expired — keep the session alive
        // Set up auto-refresh timer for future expiry
        authService.setupAutoRefresh();
      } catch (err) {
        console.error("Token validation failed:", err);
      }
    };

    if (isAuthenticated) {
      validateAuth();
    }

    // Periodically check token validity every 14 minutes
    const intervalId = setInterval(
      () => {
        if (!authService.isAuthenticated()) {
          setUser(null);
          setIsAuthenticated(false);
          setProfile(null);
          setPreferences(null);
          setStats(null);
        }
      },
      14 * 60 * 1000,
    );

    return () => clearInterval(intervalId);
  }, [isAuthenticated]);

  const value: UserContextState = {
    isAuthenticated,
    isLoading,
    error,
    user,
    profile,
    preferences,
    stats,
    login,
    logout,
    register,
    updateProfile,
    updatePreferences,
    updateAvatar,
    refreshProfile,
    refreshStats,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
};

export default UserContext;
